import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate, fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import {
  X, FileText, CheckCircle2, Circle, AlertTriangle, Loader2, Download,
  ChevronLeft, ChevronRight, Columns2, FileSearch2, Flag, Keyboard, Lock,
} from "lucide-react";

/**
 * Full-screen side-by-side verifier.
 *
 * Left pane: original uploaded statement rendered via `<iframe>` (PDFs render
 * natively; CSV/XLSX fall back to a short preview table parsed from the first
 * ~500 rows via the existing /statements/{id}/preview endpoint).
 *
 * Right pane: parsed transactions for that statement with per-row verify
 * checkbox and a "mark all" shortcut. Highlights balance deltas when the
 * statement reports a running balance we can diff against the computed one.
 */
export default function ReconciliationVerifyPanel({
  bankAccountId, start, end, onClose,
}) {
  const [statements, setStatements] = useState([]);
  const [statementIdx, setStatementIdx] = useState(0);
  const [txns, setTxns] = useState([]);
  const [loadingStmts, setLoadingStmts] = useState(true);
  const [loadingTxns, setLoadingTxns] = useState(false);
  const [fileState, setFileState] = useState({ loading: false, url: null, type: null, error: null });
  const [busyRowId, setBusyRowId] = useState(null);
  const [filterUnverified, setFilterUnverified] = useState(false);
  const [activeRowIdx, setActiveRowIdx] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);
  // Flag templates (one-click chip suggestions in the "Add note" modal)
  const [flagTemplates, setFlagTemplates] = useState([]);
  const [flagModal, setFlagModal] = useState(null); // { tx, note }
  // PDF thumbnail strip (lazy-rendered via pdfjs-dist)
  const [pdfThumbs, setPdfThumbs] = useState([]); // [{ page: 1, url: dataUrl }]
  const [pdfThumbsLoading, setPdfThumbsLoading] = useState(false);
  const [pdfCurrentPage, setPdfCurrentPage] = useState(1);
  // Locked period cache so we can short-circuit note-only edits and show
  // a lock icon on rows that fall inside a frozen month.
  const [lockedMonths, setLockedMonths] = useState(new Set());
  const blobUrlRef = useRef(null);
  const iframeRef = useRef(null);
  const csvScrollRef = useRef(null);
  const rowRefs = useRef({});

  // Load the list of statements for this bank account, newest first.
  useEffect(() => {
    const run = async () => {
      setLoadingStmts(true);
      try {
        const r = await api.get("/statements", { params: { bank_account_id: bankAccountId, limit: 500 } });
        // Keep statements whose period overlaps the selected range (unknown periods stay).
        setStatements((r.data || []).filter((s) =>
          (!end || !s.period_start || s.period_start <= end) &&
          (!start || !s.period_end || s.period_end >= start)));
      } catch {
        toast.error("Failed to load statements");
      } finally {
        setLoadingStmts(false);
      }
    };
    if (bankAccountId) run();
    // Flag templates + locked periods are tenant-wide; grab them once per
    // panel open so chip suggestions and the lock icon are available.
    (async () => {
      try {
        const [tplRes, lockRes] = await Promise.all([
          api.get("/flag-templates"),
          api.get("/locked-periods"),
        ]);
        setFlagTemplates(tplRes.data || []);
        setLockedMonths(new Set((lockRes.data || []).map((p) => `${p.year}-${String(p.month).padStart(2, "0")}`)));
      } catch { /* non-fatal */ }
    })();
    return () => {
      // Revoke any blob URL we created so Chrome doesn't leak memory.
      if (blobUrlRef.current) {
        window.URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [bankAccountId]);

  const current = statements[statementIdx];

  // When the selected statement changes, (1) refetch the parsed transactions
  // tied to it and (2) stream the raw file as a blob URL so the iframe can
  // render it with the Authorization header baked in.
  useEffect(() => {
    const run = async () => {
      if (!current) {
        setTxns([]);
        return;
      }
      // --- 1. transactions ---
      setLoadingTxns(true);
      try {
        const r = await api.get("/transactions", {
          params: { statement_id: current.id, limit: 2000, ...(start ? { start } : {}), ...(end ? { end } : {}) },
        });
        setTxns(r.data || []);
      } catch {
        toast.error("Failed to load transactions");
      } finally {
        setLoadingTxns(false);
      }
      // --- 2. raw file ---
      if (blobUrlRef.current) {
        window.URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      if (!current.storage_path) {
        setFileState({ loading: false, url: null, type: current.file_type || null, error: "Original file not archived (uploaded before archiving was enabled)." });
        return;
      }
      setFileState({ loading: true, url: null, type: current.file_type || null, error: null });
      try {
        const res = await api.get(`/statements/${current.id}/download`, {
          responseType: "blob",
          params: { disposition: "inline" },
        });
        const mime = current.file_type === "pdf"
          ? "application/pdf"
          : current.file_type === "csv"
          ? "text/csv"
          : current.file_type === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : res.data.type || "application/octet-stream";
        const blob = new Blob([res.data], { type: mime });
        const url = window.URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setFileState({ loading: false, url, type: current.file_type || null, error: null });
        setPdfThumbs([]);
        setPdfCurrentPage(1);
        // Lazy-render page thumbnails for PDFs. Imported dynamically so the
        // pdfjs worker only ships on this route.
        if (current.file_type === "pdf") {
          setPdfThumbsLoading(true);
          try {
            const pdfjs = await import("pdfjs-dist/build/pdf");
            // Vite/CRA both resolve the ESM worker URL from this import.
            const workerSrc = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
            pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
            const loadingTask = pdfjs.getDocument({ data: await blob.arrayBuffer() });
            const pdf = await loadingTask.promise;
            const maxPages = Math.min(pdf.numPages, 40);  // cap for perf
            const thumbs = [];
            for (let p = 1; p <= maxPages; p++) {
              const page = await pdf.getPage(p);
              const vp = page.getViewport({ scale: 0.18 });
              const canvas = document.createElement("canvas");
              canvas.width = vp.width;
              canvas.height = vp.height;
              const ctx = canvas.getContext("2d");
              await page.render({ canvasContext: ctx, viewport: vp }).promise;
              thumbs.push({ page: p, url: canvas.toDataURL("image/jpeg", 0.6) });
              // Yield to the main thread so the panel stays responsive on
              // long statements (10+ pages).
              if (p % 4 === 0) await new Promise((r) => setTimeout(r, 0));
            }
            setPdfThumbs(thumbs);
          } catch (e) {
            // Falls back to the native iframe prev/next without thumbnails.
            console.warn("PDF thumbnail rendering failed", e);
          } finally {
            setPdfThumbsLoading(false);
          }
        }
      } catch (err) {
        setFileState({
          loading: false, url: null, type: current.file_type || null,
          error: err?.response?.data?.detail || "Failed to load statement file",
        });
      }
    };
    run();
    // eslint-disable-next-line
  }, [current?.id]);

  const sortedTxns = useMemo(() => {
    const arr = [...txns].sort((a, b) => {
      // Sort oldest first so running balance reads top-to-bottom like a statement
      if ((a.date || "") !== (b.date || "")) return (a.date || "").localeCompare(b.date || "");
      return (a.created_at || "").localeCompare(b.created_at || "");
    });
    return filterUnverified ? arr.filter((t) => !t.is_reconciled) : arr;
  }, [txns, filterUnverified]);

  // Computed balance just before this statement's first in-range transaction.
  const [beginning, setBeginning] = useState(null);
  useEffect(() => {
    setBeginning(null);
    const first = txns.reduce((m, t) => (t.date && (!m || t.date < m) ? t.date : m), "");
    if (!first || !bankAccountId) return;
    let alive = true;
    api.get("/reports/reconciliation/detail", { params: { bank_account_id: bankAccountId, start: first } })
      .then((r) => { if (alive) setBeginning(r.data.summary.opening_balance); })
      .catch(() => {});
    return () => { alive = false; };
  }, [txns.length, current?.id, bankAccountId]); // eslint-disable-line

  const totals = useMemo(() => {
    const verified = txns.filter((t) => t.is_reconciled).length;
    const credits = txns.filter((t) => (t.amount || 0) > 0).reduce((n, t) => n + t.amount, 0);
    const debits = txns.filter((t) => (t.amount || 0) < 0).reduce((n, t) => n + t.amount, 0);
    return { verified, total: txns.length, credits, debits, net: credits + debits };
  }, [txns]);

  // Reset the active-row cursor whenever the visible list changes so the
  // keyboard-nav highlight doesn't drift off-screen on the next statement.
  useEffect(() => {
    setActiveRowIdx(0);
  }, [current?.id, filterUnverified]);

  // Jump the PDF/CSV preview to the matching amount when the user focuses a
  // row. For PDFs we use the Chromium PDF viewer's `#search=` hash parameter
  // (the viewer runs `window.find` under the hood). For CSVs we just scroll
  // the previewer's cloned row into view.
  const highlightInPreview = useCallback((tx) => {
    if (!tx) return;
    const abs = Math.abs(tx.amount || 0).toFixed(2);
    if (fileState.type === "pdf" && iframeRef.current && blobUrlRef.current) {
      // Chrome's PDF viewer: #search=<q>&phrase=true. Setting the hash
      // without touching the base URL just re-triggers the viewer's finder.
      try {
        const base = iframeRef.current.src.split("#")[0];
        iframeRef.current.src = `${base}#search=${encodeURIComponent(abs)}&phrase=true`;
      } catch { /* best-effort */ }
    } else if (fileState.type === "csv" && csvScrollRef.current) {
      // Find the first CSV row cell that contains the amount string and
      // scroll it into view.
      const container = csvScrollRef.current;
      const cells = container.querySelectorAll("td");
      for (const cell of cells) {
        if ((cell.textContent || "").includes(abs)) {
          cell.scrollIntoView({ behavior: "smooth", block: "center" });
          cell.classList.add("ll-flash");
          window.setTimeout(() => cell.classList.remove("ll-flash"), 1500);
          break;
        }
      }
    }
  }, [fileState.type]);

  const focusRow = useCallback((idx, visible) => {
    const list = visible;
    if (!list || list.length === 0) return;
    const safeIdx = Math.max(0, Math.min(list.length - 1, idx));
    setActiveRowIdx(safeIdx);
    const row = list[safeIdx];
    const node = rowRefs.current[row.id];
    if (node) node.scrollIntoView({ behavior: "smooth", block: "nearest" });
    highlightInPreview(row);
  }, [highlightInPreview]);

  const toggleVerified = async (tx) => {
    const next = !tx.is_reconciled;
    setBusyRowId(tx.id);
    try {
      const { data } = await api.patch(`/transactions/${tx.id}`, { is_reconciled: next });
      // Replace the txn with the full server response so audit stamps
      // (reconciled_by_name / reconciled_at) are reflected in the tooltip.
      setTxns((arr) => arr.map((x) => x.id === tx.id ? { ...x, ...data } : x));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "period_locked") {
        toast.error(detail.message || "This period is closed");
      } else {
        toast.error((typeof detail === "string" ? detail : detail?.message) || "Update failed");
      }
    } finally {
      setBusyRowId(null);
    }
  };

  const flagRow = (tx) => {
    // Open the chip-picker modal rather than a bare prompt so users can
    // pick a saved template with one tap. Free-text still works for anything
    // that doesn't fit a template.
    setFlagModal({ tx, note: tx.notes || "" });
  };
  const submitFlag = async () => {
    if (!flagModal) return;
    const { tx, note } = flagModal;
    try {
      await api.patch(`/transactions/${tx.id}`, { notes: note });
      setTxns((arr) => arr.map((x) => x.id === tx.id ? { ...x, notes: note } : x));
      toast.success(note ? "Note saved" : "Flag cleared");
      setFlagModal(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const isLocked = (dateIso) => {
    if (!dateIso || dateIso.length < 7) return false;
    return lockedMonths.has(dateIso.slice(0, 7));
  };

  // Keyboard shortcuts for the entire modal:
  //   ↑ / ↓       navigate rows
  //   Space / V   toggle verify on the active row + advance to the next
  //   ← / →       previous / next statement
  //   F           flag the active row with a note
  //   ?           open the shortcut cheatsheet
  //   Esc         close the panel
  useEffect(() => {
    const onKey = async (e) => {
      // Ignore when the user is typing in an input/textarea so Space in a
      // note prompt doesn't accidentally toggle verify.
      const tag = (document.activeElement?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.key === "Escape") { onClose(); return; }
      if (!sortedTxns.length && !["ArrowLeft", "ArrowRight", "?"].includes(e.key)) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        focusRow(activeRowIdx + 1, sortedTxns);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        focusRow(activeRowIdx - 1, sortedTxns);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (statementIdx < statements.length - 1) setStatementIdx(statementIdx + 1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (statementIdx > 0) setStatementIdx(statementIdx - 1);
      } else if (e.key === " " || e.key.toLowerCase() === "v") {
        e.preventDefault();
        const tx = sortedTxns[activeRowIdx];
        if (tx) {
          await toggleVerified(tx);
          // After verifying, advance to the next row to speed up a close.
          const nextIdx = Math.min(sortedTxns.length - 1, activeRowIdx + 1);
          focusRow(nextIdx, sortedTxns);
        }
      } else if (e.key.toLowerCase() === "f") {
        const tx = sortedTxns[activeRowIdx];
        if (tx) flagRow(tx);
      } else if (e.key === "?") {
        setHelpOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [activeRowIdx, sortedTxns, statementIdx, statements.length]);

  const markAllVerified = async () => {
    const pending = txns.filter((t) => !t.is_reconciled);
    if (pending.length === 0) return;
    if (!window.confirm(`Mark all ${pending.length} remaining transaction(s) as verified?`)) return;
    try {
      await Promise.all(
        pending.map((t) => api.patch(`/transactions/${t.id}`, { is_reconciled: true })),
      );
      setTxns((arr) => arr.map((x) => ({ ...x, is_reconciled: true })));
      toast.success(`${pending.length} marked verified`);
    } catch (err) {
      toast.error("Some updates failed — refresh and try again");
    }
  };

  const canPrev = statementIdx < statements.length - 1;
  const canNext = statementIdx > 0;

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#0b5d9e] flex flex-col"
      data-testid="recon-verify-panel"
      onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
      tabIndex={-1}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 text-white gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Columns2 size={18} className="shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest opacity-60">
              Side-by-side verify
              {(start || end) && (
                <span className="ml-2 normal-case tracking-normal font-mono-fin" data-testid="recon-verify-range">
                  · {start ? fmtDate(start) : "Beginning"} – {end ? fmtDate(end) : "Present"}
                </span>
              )}
            </div>
            <div className="font-display font-bold text-sm truncate">
              {current ? (current.file_name || "Statement") : "Pick a statement to begin"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {statements.length > 0 && (
            <>
              <button
                onClick={() => setStatementIdx((i) => Math.min(statements.length - 1, i + 1))}
                disabled={!canPrev}
                data-testid="recon-verify-prev"
                className="p-1.5 border border-white/20 hover:bg-white/10 disabled:opacity-40"
                title="Older statement"
              >
                <ChevronLeft size={14} />
              </button>
              <select
                value={current?.id || ""}
                onChange={(e) => {
                  const idx = statements.findIndex((s) => s.id === e.target.value);
                  if (idx >= 0) setStatementIdx(idx);
                }}
                data-testid="recon-verify-statement-select"
                className="bg-white/10 border border-white/20 text-white text-xs px-2 py-1 max-w-[220px] md:max-w-[260px]"
              >
                {statements.map((s) => (
                  <option key={s.id} value={s.id} className="text-black">
                    {s.storage_path ? "" : "⚠ "}{s.period_start && s.period_end ? `${fmtDate(s.period_start)}–${fmtDate(s.period_end)}` : fmtDate(s.uploaded_at)} · {s.file_name || s.id.slice(0, 8)}
                  </option>
                ))}
              </select>
              <button
                onClick={() => setStatementIdx((i) => Math.max(0, i - 1))}
                disabled={!canNext}
                data-testid="recon-verify-next"
                className="p-1.5 border border-white/20 hover:bg-white/10 disabled:opacity-40"
                title="Newer statement"
              >
                <ChevronRight size={14} />
              </button>
            </>
          )}
          <button
            onClick={() => setHelpOpen(true)}
            data-testid="recon-verify-help"
            aria-label="Keyboard shortcuts"
            title="Keyboard shortcuts (?)"
            className="p-1.5 border border-white/20 hover:bg-white/10"
          >
            <Keyboard size={14} />
          </button>
          <button
            onClick={onClose}
            data-testid="recon-verify-close"
            aria-label="Close"
            className="p-1.5 border border-white/20 hover:bg-white/10"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {loadingStmts ? (
        <div className="flex-1 grid place-items-center text-white/70">
          <div className="flex items-center gap-2 text-sm"><Loader2 className="animate-spin" size={16} /> Loading statements…</div>
        </div>
      ) : statements.length === 0 ? (
        <div className="flex-1 grid place-items-center text-white/70">
          <div className="text-center">
            <FileSearch2 size={28} className="mx-auto mb-2 opacity-60" />
            <div className="text-sm">{start || end ? "No statements overlap the selected date range." : "No statements uploaded for this account yet."}</div>
          </div>
        </div>
      ) : (
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 min-h-0">
          {/* LEFT PANE — statement file */}
          <div className="bg-white border-r border-white/10 min-h-0 flex flex-col" data-testid="recon-verify-left">
            <div className="px-4 py-2 border-b flex items-center justify-between text-xs" style={{ borderColor: "var(--ll-border)" }}>
              <div className="flex items-center gap-2">
                <FileText size={13} className="opacity-60" />
                <span className="font-medium">{current?.file_name || "statement"}</span>
                {current?.file_type && (
                  <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5" style={{ background: "var(--ll-mint)" }}>
                    {current.file_type}
                  </span>
                )}
              </div>
              {current && (
                <a
                  href={fileState.url || "#"}
                  download={current.file_name}
                  onClick={(e) => { if (!fileState.url) e.preventDefault(); }}
                  className="text-[10px] uppercase tracking-widest flex items-center gap-1 hover:underline"
                  data-testid="recon-verify-download"
                >
                  <Download size={11} /> Download
                </a>
              )}
            </div>
            <div className="flex-1 relative overflow-hidden">
              {fileState.loading && (
                <div className="absolute inset-0 grid place-items-center text-sm text-[var(--ll-ink-soft)]">
                  <div className="flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading file…</div>
                </div>
              )}
              {!fileState.loading && fileState.error && (
                <div className="absolute inset-0 grid place-items-center p-6">
                  <div className="max-w-sm text-center" data-testid="recon-verify-no-file">
                    <FileSearch2 size={32} className="mx-auto mb-3 opacity-40" />
                    <div className="font-display font-bold text-base mb-1.5">Original file not available</div>
                    <p className="text-xs text-[var(--ll-ink-soft)] mb-4 leading-relaxed">
                      This statement was uploaded before file archiving was enabled, so we don&apos;t have the original PDF/CSV to compare side-by-side. All <b>new uploads</b> are archived automatically.
                    </p>
                    <div className="text-[11px] text-left bg-[var(--ll-mint)]/40 border p-3 mb-3" style={{ borderColor: "var(--ll-border)" }}>
                      <div className="font-medium mb-1 flex items-center gap-1"><FileText size={11}/> To enable side-by-side on this period</div>
                      <ol className="list-decimal pl-4 space-y-0.5 text-[var(--ll-ink-soft)]">
                        <li>Open the <b>Statements</b> page in a new tab</li>
                        <li>Delete this statement (it&apos;ll remove its transactions)</li>
                        <li>Re-upload the same file — it will be archived this time</li>
                      </ol>
                    </div>
                    {statements.length > 1 && (
                      <button
                        onClick={() => {
                          const nextIdx = statements.findIndex((s, i) => i !== statementIdx && s.storage_path);
                          if (nextIdx >= 0) setStatementIdx(nextIdx);
                          else toast.info("No other archived statements on this account");
                        }}
                        data-testid="recon-verify-jump-archived"
                        className="text-xs px-3 py-1.5 border hover:bg-[var(--ll-mint)]"
                        style={{ borderColor: "var(--ll-border)" }}
                      >
                        Jump to an archived statement →
                      </button>
                    )}
                  </div>
                </div>
              )}
              {fileState.url && (
                fileState.type === "pdf" ? (
                  <div className="w-full h-full flex">
                    {/* PDF thumbnail strip — only shown when multi-page */}
                    {(pdfThumbs.length > 1 || pdfThumbsLoading) && (
                      <div
                        className="w-24 shrink-0 overflow-y-auto border-r bg-[var(--ll-bg-sidebar)]/40"
                        style={{ borderColor: "var(--ll-border)" }}
                        data-testid="recon-verify-pdf-thumbs"
                      >
                        {pdfThumbsLoading && pdfThumbs.length === 0 && (
                          <div className="p-3 text-center text-[10px] text-[var(--ll-ink-soft)]">
                            <Loader2 size={12} className="mx-auto animate-spin mb-1" />
                            Rendering…
                          </div>
                        )}
                        <ul className="p-1 space-y-1">
                          {pdfThumbs.map((t) => (
                            <li key={t.page}>
                              <button
                                onClick={() => {
                                  setPdfCurrentPage(t.page);
                                  if (iframeRef.current && blobUrlRef.current) {
                                    const base = iframeRef.current.src.split("#")[0];
                                    iframeRef.current.src = `${base}#page=${t.page}`;
                                  }
                                }}
                                data-testid={`recon-verify-pdf-thumb-${t.page}`}
                                className={`block w-full p-1 border transition-colors ${
                                  pdfCurrentPage === t.page
                                    ? "border-[var(--ll-ink)] shadow"
                                    : "border-transparent hover:border-[var(--ll-border)]"
                                }`}
                                title={`Jump to page ${t.page}`}
                              >
                                <img src={t.url} alt={`Page ${t.page}`} className="w-full h-auto" draggable={false} />
                                <div className="text-[9px] mt-0.5 font-mono-fin text-[var(--ll-ink-soft)]">
                                  {t.page}
                                </div>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <iframe
                      ref={iframeRef}
                      src={fileState.url}
                      title="Statement preview"
                      className="flex-1 h-full border-0"
                      data-testid="recon-verify-iframe"
                    />
                  </div>
                ) : fileState.type === "csv" ? (
                  <CsvViewer url={fileState.url} scrollRef={csvScrollRef} />
                ) : (
                  <div className="p-6 text-sm text-[var(--ll-ink-soft)]">
                    <p className="mb-3">
                      Browser preview isn&apos;t available for {fileState.type?.toUpperCase() || "this file type"}.
                      Click <b>Download</b> above to open it in a spreadsheet app for comparison.
                    </p>
                  </div>
                )
              )}
            </div>
          </div>

          {/* RIGHT PANE — parsed transactions */}
          <div className="bg-white min-h-0 flex flex-col" data-testid="recon-verify-right">
            <div className="px-4 py-2 border-b flex items-center justify-between gap-3 text-xs flex-wrap" style={{ borderColor: "var(--ll-border)" }}>
              <div className="flex items-center gap-3 min-w-0">
                <span className="font-medium whitespace-nowrap">
                  {loadingTxns ? "Loading…" : `${totals.verified} / ${totals.total} verified`}
                </span>
                {totals.total > 0 && (
                  <ProgressBar value={totals.verified} max={totals.total} />
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <label className="flex items-center gap-1 cursor-pointer whitespace-nowrap" data-testid="recon-verify-filter-toggle">
                  <input
                    type="checkbox"
                    checked={filterUnverified}
                    onChange={(e) => setFilterUnverified(e.target.checked)}
                    className="accent-[var(--ll-ink)]"
                  />
                  Unverified only
                </label>
                <button
                  onClick={markAllVerified}
                  disabled={totals.verified === totals.total || totals.total === 0}
                  data-testid="recon-verify-mark-all"
                  className="px-3 py-1 border text-[11px] hover:bg-[var(--ll-mint)] disabled:opacity-50 whitespace-nowrap"
                  style={{ borderColor: "var(--ll-border)" }}
                >
                  Mark all verified
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loadingTxns ? (
                <div className="p-6 text-center text-sm text-[var(--ll-ink-soft)]">
                  <Loader2 className="mx-auto animate-spin mb-2" size={16} /> Loading transactions…
                </div>
              ) : sortedTxns.length === 0 ? (
                <div className="p-10 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="recon-verify-empty">
                  {filterUnverified
                    ? "Nothing left to verify in this statement."
                    : "No transactions tied to this statement."}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-white bg-[#0b5d9e] border-b sticky top-0" style={{ borderColor: "var(--ll-border)" }}>
                      <th className="px-3 py-2 w-10 text-center">✓</th>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Description</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2 w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTxns.map((t, idx) => (
                      <tr
                        key={t.id}
                        ref={(el) => { rowRefs.current[t.id] = el; }}
                        onClick={() => focusRow(idx, sortedTxns)}
                        data-testid={`recon-verify-row-${t.id}`}
                        data-active={idx === activeRowIdx || undefined}
                        className={`border-b cursor-pointer transition-colors ${
                          idx === activeRowIdx
                            ? "bg-[var(--ll-ochre)]/30 ring-1 ring-[var(--ll-ink)] ring-inset"
                            : t.is_reconciled
                              ? "bg-[var(--ll-mint)]/40 hover:bg-[var(--ll-mint)]/60"
                              : "hover:bg-[var(--ll-bg-sidebar)]"
                        }`}
                        style={{ borderColor: "var(--ll-border)" }}
                      >
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleVerified(t); }}
                            disabled={busyRowId === t.id}
                            data-testid={`recon-verify-check-${t.id}`}
                            aria-label={t.is_reconciled ? "Un-verify" : "Mark verified"}
                            title={t.is_reconciled && t.reconciled_by_name
                              ? `Verified by ${t.reconciled_by_name} on ${fmtDate(t.reconciled_at)}`
                              : "Click to verify"}
                            className="disabled:opacity-50"
                          >
                            {busyRowId === t.id ? (
                              <Loader2 size={14} className="animate-spin inline" />
                            ) : t.is_reconciled ? (
                              <CheckCircle2 size={16} className="text-[var(--ll-success)]" />
                            ) : (
                              <Circle size={16} className="text-[var(--ll-ink-muted)]" />
                            )}
                          </button>
                        </td>
                        <td className="px-3 py-2 font-mono-fin text-xs whitespace-nowrap">
                          {fmtDate(t.date)}
                          {isLocked(t.date) && (
                            <Lock
                              size={10}
                              className="inline ml-1 text-[var(--ll-ink-muted)]"
                              aria-label="Period closed"
                              data-testid={`recon-verify-locked-${t.id}`}
                            >
                              <title>Closed period — this transaction is read-only</title>
                            </Lock>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="truncate max-w-xs" title={t.description}>{t.description}</div>
                          {t.is_reconciled && t.reconciled_by_name && (
                            <div className="text-[10px] text-[var(--ll-ink-muted)] mt-0.5" data-testid={`recon-verify-audit-${t.id}`}>
                              <CheckCircle2 size={9} className="inline mr-1 text-[var(--ll-success)]" />
                              {t.reconciled_by_name} · {fmtDate(t.reconciled_at)}
                            </div>
                          )}
                          {t.notes && (
                            <div className="text-[10px] text-[var(--ll-warning)] mt-0.5 flex items-center gap-1">
                              <Flag size={9} /> {t.notes}
                            </div>
                          )}
                        </td>
                        <td className={`px-3 py-2 font-mono-fin text-right text-xs ${t.amount < 0 ? "text-[var(--ll-error)]" : "text-[var(--ll-success)]"}`}>
                          {fmtMoney(t.amount)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={(e) => { e.stopPropagation(); flagRow(t); }}
                            data-testid={`recon-verify-flag-${t.id}`}
                            title="Add a note / flag"
                            className={`p-1 hover:text-[var(--ll-warning)] ${t.notes ? "text-[var(--ll-warning)] opacity-100" : "opacity-40 hover:opacity-100"}`}
                          >
                            <Flag size={11} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {/* Footer totals */}
            {totals.total > 0 && (
              <div className="px-4 py-2 border-t flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-[var(--ll-ink-soft)]" style={{ borderColor: "var(--ll-border)" }} data-testid="recon-verify-footer">
                <div>Beginning Balance <span className="font-mono-fin text-[var(--ll-ink)] ml-1" data-testid="recon-verify-beginning">{beginning == null ? "…" : fmtMoney(beginning)}</span></div>
                <div>Credits <span className="font-mono-fin text-[var(--ll-success)] ml-1" data-testid="recon-verify-credits">{fmtMoney(totals.credits)}</span></div>
                <div>Debits <span className="font-mono-fin text-[var(--ll-error)] ml-1" data-testid="recon-verify-debits">{fmtMoney(totals.debits)}</span></div>
                <div className="font-medium">Ending Balance <span className="font-mono-fin text-[var(--ll-ink)] ml-1" data-testid="recon-verify-ending">{beginning == null ? "…" : fmtMoney(beginning + totals.net)}</span></div>
                <StatementCheck ending={beginning == null ? null : beginning + totals.net} statementEnding={current?.closing_balance} />
              </div>
            )}
          </div>
        </div>
      )}
      {flagModal && (
        <div
          className="fixed inset-0 z-[70] bg-black/50 grid place-items-center p-4"
          onClick={() => setFlagModal(null)}
          data-testid="recon-verify-flag-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white border w-full max-w-md p-6"
            style={{ borderColor: "var(--ll-ink)" }}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Follow-up note</div>
                <h2 className="font-display font-bold text-xl flex items-center gap-2">
                  <Flag size={16} /> Flag transaction
                </h2>
                <p className="text-xs text-[var(--ll-ink-soft)] mt-1 truncate max-w-sm" title={flagModal.tx.description}>
                  {flagModal.tx.description}
                </p>
              </div>
              <button onClick={() => setFlagModal(null)} aria-label="Close" className="text-[var(--ll-ink-soft)]">
                <X size={16} />
              </button>
            </div>
            {flagTemplates.length > 0 && (
              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1.5">
                  Quick picks
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="recon-verify-flag-chips">
                  {flagTemplates.map((tpl) => (
                    <button
                      key={tpl.id}
                      type="button"
                      onClick={() => setFlagModal((m) => ({ ...m, note: tpl.label }))}
                      data-testid={`recon-verify-flag-chip-${tpl.id}`}
                      className={`text-xs px-2 py-1 border transition-colors ${
                        flagModal.note === tpl.label
                          ? "bg-[var(--ll-ink)] text-white border-[var(--ll-ink)]"
                          : "hover:bg-[var(--ll-mint)]"
                      }`}
                      style={{ borderColor: "var(--ll-border)" }}
                    >
                      {tpl.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Note</span>
              <textarea
                value={flagModal.note}
                onChange={(e) => setFlagModal((m) => ({ ...m, note: e.target.value }))}
                rows={3}
                autoFocus
                data-testid="recon-verify-flag-note"
                className="mt-1 w-full px-3 py-2 border text-sm"
                style={{ borderColor: "var(--ll-border)" }}
                placeholder="Describe what needs follow-up…"
              />
            </label>
            <div className="pt-4 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setFlagModal((m) => ({ ...m, note: "" }))}
                data-testid="recon-verify-flag-clear"
                className="text-xs text-[var(--ll-ink-soft)] hover:underline"
                disabled={!flagModal.note}
              >
                Clear flag
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setFlagModal(null)}
                  className="px-4 py-2 border text-sm"
                  style={{ borderColor: "var(--ll-border)" }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitFlag}
                  data-testid="recon-verify-flag-save"
                  className="px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] text-sm"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {helpOpen && (
        <div
          className="fixed inset-0 z-[70] bg-black/50 grid place-items-center p-4"
          onClick={() => setHelpOpen(false)}
          data-testid="recon-verify-help-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white border w-full max-w-md p-6"
            style={{ borderColor: "var(--ll-ink)" }}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Side-by-side verify</div>
                <h2 className="font-display font-bold text-xl flex items-center gap-2">
                  <Keyboard size={16} /> Keyboard shortcuts
                </h2>
              </div>
              <button onClick={() => setHelpOpen(false)} aria-label="Close" className="text-[var(--ll-ink-soft)]">
                <X size={16} />
              </button>
            </div>
            <ul className="divide-y text-sm" style={{ borderColor: "var(--ll-border)" }}>
              <Shortcut keys={["↑", "↓"]} label="Move between parsed transactions" />
              <Shortcut keys={["Space", "V"]} label="Verify the current row and advance" />
              <Shortcut keys={["F"]} label="Flag the current row with a note" />
              <Shortcut keys={["←", "→"]} label="Previous / next statement" />
              <Shortcut keys={["?"]} label="Show this shortcut cheatsheet" />
              <Shortcut keys={["Esc"]} label="Close the verify panel" />
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function Shortcut({ keys, label }) {
  return (
    <li className="py-2 flex items-center justify-between gap-3">
      <span className="text-[var(--ll-ink-soft)]">{label}</span>
      <span className="flex items-center gap-1">
        {keys.map((k) => (
          <kbd
            key={k}
            className="px-2 py-0.5 text-[11px] font-mono bg-[var(--ll-bg-sidebar)] border border-[var(--ll-border)]"
          >
            {k}
          </kbd>
        ))}
      </span>
    </li>
  );
}

function ProgressBar({ value, max }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="w-20 sm:w-28 h-1.5 bg-[var(--ll-bg-sidebar)] shrink-0" title={`${pct}% verified`} data-testid="recon-verify-progress">
      <div className="h-full bg-[var(--ll-success)] transition-all" style={{ width: `${pct}%` }} />
    </div>
  );
}

/**
 * Lightweight in-browser CSV viewer. Fetches the blob URL (same-origin after
 * `URL.createObjectURL`), parses up to 500 rows with a dumb RFC-ish split, and
 * renders them as a sticky-header table so the user can scan beside the parsed
 * transactions column.
 */
function CsvViewer({ url, scrollRef }) {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const text = await fetch(url).then((r) => r.text());
        const parsed = parseCsv(text, 500);
        setRows(parsed);
      } catch {
        setErr("Failed to parse CSV");
      }
    })();
  }, [url]);
  if (err) return <div className="p-6 text-sm text-[var(--ll-error)]">{err}</div>;
  if (rows.length === 0) return <div className="p-6 text-sm text-[var(--ll-ink-soft)]">Loading CSV…</div>;
  const [header, ...body] = rows;
  return (
    <div ref={scrollRef} className="h-full overflow-auto" data-testid="recon-verify-csv">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-[#0b5d9e] text-white sticky top-0">
            {header.map((h, i) => (
              <th key={i} className="px-2 py-1.5 text-left font-medium text-[10px] uppercase tracking-widest">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((r, i) => (
            <tr key={i} className="border-b" style={{ borderColor: "var(--ll-border)" }}>
              {r.map((c, j) => <td key={j} className="px-2 py-1 whitespace-nowrap">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function parseCsv(text, maxRows) {
  const out = [];
  let cur = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length && out.length < maxRows; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else field += ch;
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === ",") { cur.push(field); field = ""; }
      else if (ch === "\n") { cur.push(field); out.push(cur); cur = []; field = ""; }
      else if (ch === "\r") { /* skip */ }
      else field += ch;
    }
  }
  if (field.length || cur.length) { cur.push(field); out.push(cur); }
  return out;
}

function StatementCheck({ ending, statementEnding }) {
  if (statementEnding == null) {
    return <div className="text-[var(--ll-ink-muted)]" data-testid="recon-verify-stmt-none">No statement ending balance</div>;
  }
  if (ending == null) return null;
  const diff = Math.round((ending - statementEnding) * 100) / 100;
  if (Math.abs(diff) < 0.01) {
    return (
      <div className="inline-flex items-center gap-1 text-[var(--ll-success)] font-medium" data-testid="recon-verify-stmt-match">
        <CheckCircle2 size={12} /> Matches statement
      </div>
    );
  }
  return (
    <div className="inline-flex items-center gap-1 text-[var(--ll-error)] font-medium" data-testid="recon-verify-stmt-mismatch">
      <AlertTriangle size={12} /> Statement says {fmtMoney(statementEnding)} · off by {fmtMoney(diff)}
    </div>
  );
}
